// Syntax highlighting for the code editor.
//
// The editor is a transparent <textarea> layered over a highlighted <pre>, so
// the two must produce identical text metrics — see CodeTab in
// components/GenerationView.jsx. This module owns only the tokenising half.
//
// Prism is imported with explicit language components rather than the bundled
// "all languages" build: markup, css and clike/javascript cover every file the
// generator emits, and pulling the rest would add weight for nothing.

import Prism from 'prismjs';
import 'prismjs/components/prism-markup';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-javascript';

/**
 * Map a CodeTab file key to a Prism language id.
 *
 * Page keys are filenames (`about.html`), so anything that is not one of the
 * known asset keys is markup.
 *
 * @param {string} fileKey 'html' | 'css' | 'js' | 'icon' | '<page path>'
 * @returns {string} a key into Prism.languages
 */
export function prismLanguageFor(fileKey) {
  if (fileKey === 'css') return 'css';
  if (fileKey === 'js')  return 'javascript';
  return 'markup';   // html pages and favicon.svg alike
}

/**
 * Highlight source into HTML safe to inject.
 *
 * Prism escapes the text it tokenises, so the result contains only its own
 * <span> wrappers around escaped content. Unknown languages fall back to plain
 * escaped text rather than throwing.
 *
 * A trailing newline is preserved as a space: browsers collapse a final "\n" in
 * a <pre>, which would shift the highlighted layer out of alignment with the
 * textarea by one line whenever the file ends in a blank line.
 *
 * @param {string} code
 * @param {string} language a Prism language id
 * @returns {string} HTML
 */
export function highlightCode(code, language) {
  const source = String(code ?? '');
  const grammar = Prism.languages[language];

  const html = grammar
    ? Prism.highlight(source, grammar, language)
    : escapeHtml(source);

  return html.endsWith('\n') ? `${html} ` : html;
}

function escapeHtml(s) {
  return s.replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
}
